import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { DashboardStats, Procedure } from '../types';
import {
  DocumentTextIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';

// ─── Skeleton helpers ────────────────────────────────────────────────────────

const SkeletonBox: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-xl ${className}`} />
);

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-8">
    {/* Header skeleton */}
    <div className="flex items-start justify-between">
      <div className="space-y-2">
        <SkeletonBox className="h-8 w-64" />
        <SkeletonBox className="h-4 w-80" />
      </div>
      <SkeletonBox className="h-8 w-32 rounded-full" />
    </div>

    {/* KPI cards skeleton */}
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <div className="flex items-center gap-4">
            <SkeletonBox className="h-14 w-14 rounded-2xl" />
            <div className="space-y-2 flex-1">
              <SkeletonBox className="h-3 w-20" />
              <SkeletonBox className="h-8 w-12" />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Status overview skeleton */}
    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
      <SkeletonBox className="h-5 w-48" />
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <SkeletonBox key={i} className="h-24 rounded-xl" />
        ))}
      </div>
    </div>

    {/* Procedures skeleton */}
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex justify-between">
        <SkeletonBox className="h-5 w-40" />
        <SkeletonBox className="h-4 w-20" />
      </div>
      {[...Array(4)].map((_, i) => (
        <div key={i} className="px-6 py-4 border-b border-gray-50 flex items-center gap-4">
          <SkeletonBox className="h-10 w-10 rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBox className="h-4 w-48" />
            <SkeletonBox className="h-3 w-32" />
          </div>
          <div className="space-y-1 text-right">
            <SkeletonBox className="h-2 w-28 rounded-full" />
            <SkeletonBox className="h-3 w-16 ml-auto" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getRoleBadge = (role?: string) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return { label: 'Super Admin', className: 'bg-purple-100 text-purple-700 border border-purple-200' };
    case 'RESPONSABLE':
      return { label: 'Responsable', className: 'bg-blue-100 text-blue-700 border border-blue-200' };
    default:
      return { label: 'Utilisateur', className: 'bg-gray-100 text-gray-600 border border-gray-200' };
  }
};

const getProgressColor = (progress: number) => {
  if (progress === 100) return 'bg-green-500';
  if (progress >= 60) return 'bg-blue-500';
  if (progress >= 30) return 'bg-yellow-500';
  return 'bg-red-400';
};

const getProgressBadge = (progress: number) => {
  if (progress === 100) return { label: 'Terminée', className: 'bg-green-100 text-green-700' };
  if (progress > 0) return { label: 'En cours', className: 'bg-blue-100 text-blue-700' };
  return { label: 'En attente', className: 'bg-gray-100 text-gray-600' };
};

const formatDate = () => {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
};

// ─── Component ────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, proceduresData] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getProcedures({ page: 0, size: 5 }),
        ]);
        setStats(statsData);
        setProcedures(proceduresData.content);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const roleBadge = getRoleBadge(user?.role);

  const statCards = [
    {
      name: 'Total Procédures',
      value: stats?.totalProcedures || 0,
      icon: DocumentTextIcon,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      accent: 'border-t-4 border-blue-400',
      description: 'procédures enregistrées',
    },
    {
      name: 'Structures',
      value: stats?.totalStructures || 0,
      icon: BuildingOfficeIcon,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      accent: 'border-t-4 border-purple-400',
      description: 'structures actives',
      show: isSuperAdmin(),
    },
    {
      name: 'Tâches en retard',
      value: stats?.tachesEnRetard || 0,
      icon: ExclamationTriangleIcon,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      accent: 'border-t-4 border-red-400',
      description: 'nécessitent attention',
    },
    {
      name: 'Échéances proches',
      value: stats?.tachesProchesEcheance || 0,
      icon: ClockIcon,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      accent: 'border-t-4 border-amber-400',
      description: 'dans les 7 prochains jours',
    },
  ].filter((card) => card.show !== false);

  const statusCards = [
    {
      name: 'Terminées',
      value: stats?.proceduresTerminees || 0,
      icon: CheckCircleIcon,
      iconColor: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border border-green-100',
      valueColor: 'text-green-700',
      dot: 'bg-green-500',
    },
    {
      name: 'En cours',
      value: stats?.proceduresEnCours || 0,
      icon: PlayIcon,
      iconColor: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border border-blue-100',
      valueColor: 'text-blue-700',
      dot: 'bg-blue-500',
    },
    {
      name: 'En attente',
      value: stats?.proceduresEnAttente || 0,
      icon: PauseIcon,
      iconColor: 'text-gray-500',
      bg: 'bg-gray-50',
      border: 'border border-gray-200',
      valueColor: 'text-gray-700',
      dot: 'bg-gray-400',
    },
  ];

  const hasUrgentItems =
    (stats?.tachesEnRetard || 0) > 0 || (stats?.tachesProchesEcheance || 0) > 0;

  return (
    <div className="space-y-8 pb-8">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour,&nbsp;
              <span className="text-primary-600">{user?.prenom}</span>&nbsp;
              {user?.nom}&nbsp; 
            </h1>
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${roleBadge.className}`}>
              <ShieldCheckIcon className="h-3.5 w-3.5" />
              {roleBadge.label}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-gray-500">
            {isSuperAdmin()
              ? "Vue d'ensemble de toutes les procédures de dématérialisation"
              : `Vue d'ensemble des procédures de ${user?.structureNom || 'votre structure'}`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap bg-white rounded-xl border border-gray-100 px-4 py-2 shadow-sm">
          <CalendarDaysIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="capitalize">{formatDate()}</span>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-6 ${stat.accent} hover:shadow-md transition-all duration-200 cursor-default group`}
          >
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 ${stat.iconBg} rounded-2xl p-3.5 group-hover:scale-110 transition-transform duration-200`}>
                <stat.icon className={`h-7 w-7 ${stat.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide truncate">
                  {stat.name}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1 leading-none">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-400 mt-1 truncate">{stat.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Alerte urgences ───────────────────────────────────────────────── */}
      {hasUrgentItems && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-4">
          <div className="flex-shrink-0 bg-red-100 rounded-xl p-2.5">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Points d'attention</p>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {(stats?.tachesEnRetard || 0) > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  {stats?.tachesEnRetard} tâche{(stats?.tachesEnRetard || 0) > 1 ? 's' : ''} en retard
                </span>
              )}
              {(stats?.tachesProchesEcheance || 0) > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  {stats?.tachesProchesEcheance} échéance{(stats?.tachesProchesEcheance || 0) > 1 ? 's' : ''} proche{(stats?.tachesProchesEcheance || 0) > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── État des procédures ───────────────────────────────────────────── */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-5">État des procédures</h2>
        <div className="grid grid-cols-3 gap-4">
          {statusCards.map((status) => (
            <div
              key={status.name}
              className={`${status.bg} ${status.border} rounded-xl p-5 flex flex-col items-center text-center group hover:shadow-sm transition-all duration-200`}
            >
              <div className="mb-3">
                <status.icon className={`h-8 w-8 ${status.iconColor} mx-auto`} />
              </div>
              <p className={`text-3xl font-bold ${status.valueColor} leading-none`}>
                {status.value}
              </p>
              <div className="flex items-center gap-1.5 mt-2">
                <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                <p className="text-xs font-medium text-gray-500">{status.name}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Barre de répartition globale */}
        {(stats?.totalProcedures || 0) > 0 && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
              <span>Répartition globale</span>
              <span>{stats?.totalProcedures} procédures</span>
            </div>
            <div className="flex h-2 rounded-full overflow-hidden gap-0.5">
              {(stats?.proceduresTerminees || 0) > 0 && (
                <div
                  className="bg-green-500 rounded-l-full transition-all duration-500"
                  style={{ width: `${((stats?.proceduresTerminees || 0) / (stats?.totalProcedures || 1)) * 100}%` }}
                />
              )}
              {(stats?.proceduresEnCours || 0) > 0 && (
                <div
                  className="bg-blue-500 transition-all duration-500"
                  style={{ width: `${((stats?.proceduresEnCours || 0) / (stats?.totalProcedures || 1)) * 100}%` }}
                />
              )}
              {(stats?.proceduresEnAttente || 0) > 0 && (
                <div
                  className="bg-gray-300 rounded-r-full transition-all duration-500"
                  style={{ width: `${((stats?.proceduresEnAttente || 0) / (stats?.totalProcedures || 1)) * 100}%` }}
                />
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500" />Terminées</span>
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />En cours</span>
              <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-gray-300" />En attente</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Procédures récentes ───────────────────────────────────────────── */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Procédures récentes</h2>
          <Link
            to="/procedures"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            Voir tout
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>

        {procedures.length === 0 ? (
          /* État vide illustré */
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="bg-gray-100 rounded-full p-5 mb-4">
              <InboxIcon className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">Aucune procédure pour le moment</p>
            <p className="text-xs text-gray-400 mt-1">
              Les procédures enregistrées apparaîtront ici.
            </p>
            <Link
              to="/procedures"
              className="mt-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-200"
            >
              Créer une procédure
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {procedures.map((procedure) => {
              const progressBadge = getProgressBadge(procedure.ponderationScore);
              const progressColor = getProgressColor(procedure.ponderationScore);
              return (
                <Link
                  key={procedure.id}
                  to={`/procedures/${procedure.id}`}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-all duration-200 group"
                >
                  {/* Icône */}
                  <div className="flex-shrink-0 w-11 h-11 bg-primary-50 rounded-xl flex items-center justify-center group-hover:bg-primary-100 transition-colors duration-200">
                    <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                  </div>

                  {/* Infos principales */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {procedure.nom}
                      </p>
                      <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${progressBadge.className}`}>
                        {progressBadge.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {procedure.structureNom}
                      {procedure.structureNom && procedure.annee ? ' · ' : ''}
                      {procedure.annee}
                    </p>

                    {/* Barre de progression */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`${progressColor} h-1.5 rounded-full transition-all duration-500`}
                          style={{ width: `${procedure.ponderationScore}%` }}
                        />
                      </div>
                      <span className="flex-shrink-0 text-xs font-semibold text-gray-700">
                        {procedure.ponderationScore}%
                      </span>
                    </div>
                  </div>

                  {/* Tâches */}
                  <div className="flex-shrink-0 text-right hidden sm:block">
                    <p className="text-xs text-gray-400">Tâches</p>
                    <p className="text-sm font-semibold text-gray-700 mt-0.5">
                      {procedure.tachesCompleted}
                      <span className="text-gray-400 font-normal">/{procedure.tachesTotal}</span>
                    </p>
                  </div>

                  <ArrowRightIcon className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors duration-200 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
